import { useState, useRef, useEffect } from "react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * DashboardAgent — natural-language Q&A chatbox docked in the bottom-right
 * of the dashboard. Powered by Claude reading Shannon's own MongoDB snapshot.
 *
 * Examples she can ask:
 *  - "What's the status of Weststone?"
 *  - "How many of my pre-engagement letters have been opened?"
 *  - "Any plan restructures waiting on my approval?"
 *  - "Which prospect has the worst loan situation?"
 */
const DashboardAgent = () => {
  const [open, setOpen] = useState(false);
  const [history, setHistory] = useState([
    { role: "assistant", text: "Hi Shannon. Ask me anything about your dashboard — projects, prospects, grant pipeline, emails opened, plan restructures pending. I'll only read your own data." },
  ]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [history, open]);

  const ask = async () => {
    const q = input.trim();
    if (!q || sending) return;
    setInput("");
    setHistory((prev) => [...prev, { role: "user", text: q }]);
    setSending(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/dashboard-ask`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          question: q,
          history: history.slice(-6).map(h => ({ role: h.role, text: h.text })),
        }),
      });
      const d = await r.json().catch(() => ({}));
      if (r.ok) {
        setHistory((prev) => [...prev, { role: "assistant", text: d.answer || "(no answer)" }]);
      } else {
        setHistory((prev) => [...prev, { role: "assistant", text: `⚠ ${d.detail || "Couldn't reach the agent."}` }]);
      }
    } catch (e) {
      setHistory((prev) => [...prev, { role: "assistant", text: `⚠ Network error.` }]);
    }
    setSending(false);
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      ask();
    }
  };

  if (!open) {
    return (
      <button type="button" onClick={() => setOpen(true)}
              data-testid="dashboard-agent-open"
              title="Ask the dashboard agent — reads your data and answers in your voice"
              style={{
                position: "fixed",
                /* Bottom-LEFT, respecting the mobile safe-area inset and
                   lifted well clear of the action-button band. Feb 12, 2026
                   — Shannon reported Ask Moovin overlapping the greeting +
                   action buttons on her phone; the pill is now shorter and
                   sits at a fixed 24px offset so it's out of the content
                   flow entirely. Companion FABs (?, ✎) live bottom-right. */
                bottom: "calc(env(safe-area-inset-bottom, 0px) + 24px)",
                left: 16,
                zIndex: 700,
                background: "linear-gradient(135deg, #B8915A 0%, #8a6c3e 100%)",
                color: "#1b2a4e", border: "1px solid #6a4f2a",
                padding: "0.45rem 0.75rem", borderRadius: 999,
                fontWeight: 800, fontSize: 12, cursor: "pointer",
                boxShadow: "0 10px 24px rgba(0,0,0,0.4)",
                letterSpacing: "0.02em",
                opacity: 0.94,
                maxWidth: "42vw",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis"
              }}>
        🤖 Ask Moovin
      </button>
    );
  }

  return (
    <div data-testid="dashboard-agent-panel" style={{
      position: "fixed", bottom: 20, left: 20, zIndex: 700,
      width: 380, maxWidth: "92vw", height: 520, maxHeight: "78vh",
      background: "#0f172a", border: "1px solid rgba(184,145,90,0.5)",
      borderLeft: "4px solid #B8915A", borderRadius: 12,
      display: "flex", flexDirection: "column",
      boxShadow: "0 28px 60px rgba(0,0,0,0.55)"
    }}>
      {/* Header */}
      <div style={{
        padding: "0.85rem 1rem", borderBottom: "1px solid rgba(255,255,255,0.08)",
        display: "flex", justifyContent: "space-between", alignItems: "center", gap: 6
      }}>
        <div>
          <div style={{ fontSize: 10, letterSpacing: "0.16em", textTransform: "uppercase",
                        color: "#B8915A", fontWeight: 700 }}>
            🤖 Ask Moovin
          </div>
          <div style={{ color: "#F3F4F6", fontWeight: 700, fontSize: 14, marginTop: 1 }}>
            Conversational Dashboard Agent
          </div>
        </div>
        <button type="button" onClick={() => setOpen(false)}
                data-testid="dashboard-agent-close"
                style={{ background: "transparent", border: "none", color: "#9CA3AF",
                         fontSize: 22, cursor: "pointer", lineHeight: 1 }}>×</button>
      </div>

      {/* Messages */}
      <div ref={scrollRef} style={{
        flex: 1, overflowY: "auto", padding: "0.85rem 1rem", display: "flex",
        flexDirection: "column", gap: 10
      }}>
        {history.map((h, i) => (
          <div key={i} style={{
            alignSelf: h.role === "user" ? "flex-end" : "flex-start",
            maxWidth: "85%",
            background: h.role === "user" ? "rgba(184,145,90,0.18)" : "rgba(31,41,55,0.7)",
            border: `1px solid ${h.role === "user" ? "rgba(184,145,90,0.4)" : "rgba(255,255,255,0.06)"}`,
            borderRadius: 8, padding: "0.55rem 0.8rem",
            color: "#F3F4F6", fontSize: 13.5, lineHeight: 1.55,
            whiteSpace: "pre-wrap"
          }}>
            {h.text}
          </div>
        ))}
        {sending && (
          <div style={{ alignSelf: "flex-start", color: "#9CA3AF", fontStyle: "italic", fontSize: 13 }}>
            Reading your dashboard…
          </div>
        )}
      </div>

      {/* Input */}
      <div style={{
        padding: "0.65rem 0.85rem", borderTop: "1px solid rgba(255,255,255,0.08)",
        display: "flex", gap: 6
      }}>
        <textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={onKey}
          placeholder="Ask anything: Weststone status, opens this week, restructures pending…"
          rows={1}
          data-testid="dashboard-agent-input"
          style={{
            flex: 1, background: "#1f2937", border: "1px solid #374151",
            color: "#F3F4F6", padding: "0.5rem 0.7rem", borderRadius: 6,
            fontSize: 13.5, resize: "none", fontFamily: "inherit", maxHeight: 80
          }}
        />
        <button type="button" onClick={ask} disabled={sending || !input.trim()}
                data-testid="dashboard-agent-send"
                style={{
                  background: sending || !input.trim() ? "#4B5563" : "#B8915A",
                  color: "#1b2a4e", border: "1px solid #8a6c3e",
                  padding: "0.5rem 0.9rem", borderRadius: 6, fontWeight: 700, fontSize: 13,
                  cursor: sending || !input.trim() ? "not-allowed" : "pointer"
                }}>
          {sending ? "…" : "Ask"}
        </button>
      </div>
    </div>
  );
};

export default DashboardAgent;
